import {
  DOCS_FORM_NAMES,
  DRV_TRAVEL_TYPES,
  OUTPATIENT_NOT_POSSIBLE_REASONS,
  PREDICAMENT_STATE_NO,
  PREDICAMENT_STATE_YES,
  REHAB_TRIGGERING_EVENT,
} from "core/consts";
import {
  DRG_CODE_CHARACTER_LIMIT,
  ICD_CODE_CHARACTER_LIMIT,
  IK_NUMBER_CHARACTER_LIMIT,
  PENSION_INSURANCE_NUMBER_CHARACTER_LIMIT,
} from "core/schemas/shared";
import {
  ControlledCheckbox,
  ControlledCheckboxGroup,
  ControlledDateInput,
  ControlledInput,
  ControlledRadioGroup,
  ControlledSelect,
  ControlledTextarea,
} from "ds/ui";
import { FormFieldConfig } from "..";
import {
  CarelevelSelectedField,
  InfectionsSelectedField,
  NeedsCatheterSelectedField,
  OrientationDisorientedSelectedField,
  OtherMeasuresField,
  SecondaryDiagnosisSelectedField,
} from "./fields";

const INSURANCE_DELIVERY_ADDRESS_OPTIONS = [
  {
    id: 1,
    label: "Baden-Württemberg Standort Karlsruhe",
    value: "Baden-Württemberg /76122 Karlsruhe",
  },
  {
    id: 2,
    label: "Baden-Württemberg Standort Stuttgart",
    value: "Baden-Württemberg /70429 Stuttgart",
  },
  {
    id: 3,
    label: "Bayern Süd Standort Landshut",
    value: "Bayern Süd /84024 Landshut",
  },
  {
    id: 4,
    label: "Bayern Süd Standort München",
    value: "Bayern Süd /81729 München",
  },
  {
    id: 5,
    label: "Berlin-Brandenburg Standort Berlin",
    value: "Berlin-Brandenburg /Knobelsdorffstr. 92 /14059 Berlin",
  },
  {
    id: 6,
    label: "Berlin-Brandenburg Standort Frankfurt (Oder)",
    value:
      "Berlin-Brandenburg /Bertha-von-Suttner-Str. 1 /15236 Frankfurt (Oder)",
  },
  {
    id: 7,
    label: "Braunschweig-Hannover Standort Braunschweig",
    value: "Braunschweig-Hannover /Kurt-Schumacher-Str. 20 /38102 Braunschweig",
  },
  {
    id: 8,
    label: "Braunschweig-Hannover Standort Laatzen",
    value: "Braunschweig-Hannover /Lange Weihe 6 /30880 Laatzen",
  },
  {
    id: 9,
    label: "Bund",
    value: "Bund /10704 Berlin",
  },
  {
    id: 10,
    label: "Hessen",
    value: "Hessen /Städelstr. 28 /60596 Frankfurt am Main",
  },
  {
    id: 11,
    label: "Knappschaft-Bahn-See Hauptverwaltung",
    value: "Knappschaft-Bahn-See /Pieperstr. 14-28 /44789 Bochum",
  },
  {
    id: 12,
    label: "Mitteldeutschland Standort Erfurt",
    value: "Mitteldeutschland /Kranichfelder Str. 3 /99097 Erfurt",
  },
  {
    id: 13,
    label: "Mitteldeutschland Standort Halle",
    value: "Mitteldeutschland /Paracelsusstr. 21 /06114 Halle",
  },
  {
    id: 14,
    label: "Mitteldeutschland Standort Leipzig",
    value: "Mitteldeutschland /Georg-Schumann-Str. 146 /04159 Leipzig",
  },
  {
    id: 15,
    label: "Nord Standort Hamburg",
    value: "Nord /Friedrich-Ebert-Damm 245 /22159 Hamburg",
  },
  {
    id: 16,
    label: "Nord Standort Lübeck",
    value: "Nord /Ziegelstr. 150 /23556 Lübeck",
  },
  {
    id: 17,
    label: "Nord Standort Neubrandenburg",
    value: "Nord /Platanenstr. 43 /17033 Neubrandenburg",
  },
  {
    id: 18,
    label: "Nordbayern Standort Bayreuth",
    value: "Nordbayern /95440 Bayreuth",
  },
  {
    id: 19,
    label: "Nordbayern Standort Würzburg",
    value: "Nordbayern /Friedenstr. 12/14 /97072 Würzburg",
  },
  {
    id: 20,
    label: "Oldenburg-Bremen Geschäftsstelle Bremen",
    value: "Oldenburg-Bremen /Schwachhauser Heerstr. 32-34 /28209 Bremen",
  },
  {
    id: 21,
    label: "Oldenburg-Bremen Hauptverwaltung Oldenburg",
    value: "Oldenburg-Bremen /Huntestr. 11 /26135 Oldenburg",
  },
  {
    id: 22,
    label: "Rheinland",
    value: "Rheinland /40194 Düsseldorf",
  },
  {
    id: 23,
    label: "Rheinland-Pfalz",
    value: "Rheinland-Pfalz /Eichendorffstr. 4-6 /67346 Speyer",
  },
  {
    id: 24,
    label: "Saarland",
    value: "Saarland /Martin-Luther-Str. 2-4 /66111 Saarbrücken",
  },
  {
    id: 25,
    label: "Schwaben",
    value: "Schwaben /Dieselstr. 9 /86154 Augsburg",
  },
  {
    id: 26,
    label: "Westfalen",
    value: "Westfalen /Gartenstr. 194 /48147 Münster",
  },
];

export const DRVMedicalFormSections: FormFieldConfig<
  typeof DOCS_FORM_NAMES.DRV_MEDICAL
>["sections"] = [
  // Intro Section
  {
    fields: {
      admission_date: {
        input: ({ translations }) => (
          <ControlledDateInput
            label={
              translations.patientForms.drvMedicalForm.sectionZero.intakeDate
            }
            name="admission_date"
          />
        ),
        position: 14,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.drvMedicalForm.sectionZero
              .subtitleHospital,
        },
      },
      day_reha: {
        input: ({ translations }) => (
          <ControlledCheckbox
            label={translations.patientForms.drvMedicalForm.sectionZero.mobile}
            name="day_reha"
          />
        ),
        position: 8,
      },
      discharge_date: {
        input: ({ translations }) => (
          <ControlledDateInput
            label={
              translations.patientForms.drvMedicalForm.sectionZero.dischargeDate
            }
            name="discharge_date"
          />
        ),
        position: 17,
      },
      full_reha: {
        input: ({ translations }) => (
          <ControlledCheckbox
            label={translations.patientForms.drvMedicalForm.sectionZero.static}
            name="full_reha"
          />
        ),
        position: 7,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.drvMedicalForm.sectionZero
              .subtitleSuggestedRehab,
        },
      },
      hospital_name_address: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.patientForms.drvMedicalForm.addressHospital}
            name="hospital_name_address"
            placeholder=" "
          />
        ),
        position: 2,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.drvMedicalForm.sectionZero
              .subtitleHospitalAddress,
        },
      },
      patient_birthdate: {
        input: ({ translations }) => (
          <ControlledDateInput
            label={
              translations.patientForms.drvMedicalForm.sectionZero
                .dateBirthPatient
            }
            name="patient_birthdate"
          />
        ),
        position: 10,
      },
      patient_city: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.patientForms.drvMedicalForm.sectionZero.city}
            name="patient_city"
            placeholder=" "
          />
        ),
        position: 13,
      },
      patient_name: {
        input: ({ translations }) => (
          <ControlledInput
            label={
              translations.patientForms.drvMedicalForm.sectionZero.patientName
            }
            name="patient_name"
            placeholder=" "
          />
        ),
        position: 9,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.drvMedicalForm.sectionZero
              .subtitlePatientInformation,
        },
      },
      patient_street_housenumber: {
        input: ({ translations }) => (
          <ControlledInput
            label={
              translations.patientForms.drvMedicalForm.sectionZero
                .addressPatient
            }
            name="patient_street_housenumber"
            placeholder=" "
          />
        ),
        position: 11,
      },
      patient_zipcode: {
        input: ({ translations }) => (
          <ControlledInput
            label={
              translations.patientForms.drvMedicalForm.sectionZero
                .zipCodePatient
            }
            name="patient_zipcode"
            placeholder=" "
          />
        ),
        position: 12,
      },
      pension_procedure_after_examination: {
        input: ({ translations }) => (
          <ControlledCheckbox
            label={
              translations.patientForms.drvMedicalForm.sectionZero
                .instructionAfterExamination
            }
            name="pension_procedure_after_examination"
          />
        ),
        position: 6,
      },
      pension_procedure_direct: {
        input: ({ translations }) => (
          <ControlledCheckbox
            label={
              translations.patientForms.drvMedicalForm.sectionZero
                .directDischarge
            }
            name="pension_procedure_direct"
          />
        ),
        position: 5,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.drvMedicalForm.sectionZero
              .typeOfProcedure,
        },
      },
      preferred_rehab_clinic: {
        input: ({ translations }) => (
          <ControlledInput
            label={
              translations.patientForms.drvMedicalForm.sectionZero.addressAhb
            }
            name="preferred_rehab_clinic"
            placeholder=" "
          />
        ),
        position: 4,
        subTitle: {
          className: "pb-3",
          text: ({ translations }) =>
            translations.patientForms.drvMedicalForm.sectionZero.subtitleAhb,
        },
      },
      rehab_triggering_event: {
        input: ({ translations }) => {
          const options = [
            {
              label:
                translations.patientForms.drvMedicalForm.sectionZero.surgery,
              value: REHAB_TRIGGERING_EVENT.SURGERY,
            },
            {
              label:
                translations.patientForms.drvMedicalForm.sectionZero.acuteEvent,
              value: REHAB_TRIGGERING_EVENT.ACUTE_EVENT,
            },
            {
              label:
                translations.patientForms.drvMedicalForm.sectionZero.radiation,
              value: REHAB_TRIGGERING_EVENT.RADIATION,
            },
            {
              label:
                translations.patientForms.drvMedicalForm.sectionZero
                  .antineoplasticTherapy,
              value: REHAB_TRIGGERING_EVENT.ANTINEOPLASTIC_THERAPY,
            },
            {
              label:
                translations.patientForms.drvMedicalForm.sectionZero
                  .notApplicable,
              value: REHAB_TRIGGERING_EVENT.NOT_APPLICABLE,
            },
            {
              label:
                translations.patientForms.drvMedicalForm.sectionZero
                  .accidentWork,
              value: REHAB_TRIGGERING_EVENT.ACCIDENT_WORK,
            },
            {
              label:
                translations.patientForms.drvMedicalForm.sectionZero
                  .thirdPartyDamage,
              value: REHAB_TRIGGERING_EVENT.THIRD_PARTY_DAMAGE,
            },
          ];

          return (
            <ControlledCheckboxGroup
              aria-label={
                translations.patientForms.drvMedicalForm.sectionZero
                  .ahbEventCheckboxGroupLabel
              }
              name="rehab_triggering_event"
              options={options}
              size="sm"
            />
          );
        },
        position: 16,
      },
      rehab_triggering_event_date: {
        input: ({ translations }) => (
          <ControlledDateInput
            label={
              translations.patientForms.drvMedicalForm.sectionZero.ahbEventDate
            }
            name="rehab_triggering_event_date"
          />
        ),
        position: 15,
      },
      social_worker_name_number: {
        input: ({ translations }) => (
          <ControlledInput
            label={
              translations.patientForms.drvMedicalForm.sectionZero
                .contactPersonInformation
            }
            name="social_worker_name_number"
            placeholder=" "
          />
        ),
        position: 3,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.drvMedicalForm.sectionZero
              .subtitleContactPerson,
        },
      },
      statutory_pension_insurance_number: {
        input: ({ translations }) => (
          <ControlledInput
            label={
              translations.patientForms.drvMedicalForm.sectionZero
                .insuranceNumber
            }
            maxLength={PENSION_INSURANCE_NUMBER_CHARACTER_LIMIT}
            name="statutory_pension_insurance_number"
            placeholder=" "
          />
        ),
        position: 1,
      },
      transfer_date: {
        input: ({ translations }) => (
          <ControlledDateInput
            label={
              translations.patientForms.drvMedicalForm.sectionZero.ahbStartDate
            }
            name="transfer_date"
          />
        ),
        position: 18,
      },
    },
    position: 1,
    title: () => "",
  },
  // Section One: Main Diagnosis
  {
    fields: {
      drg_code: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.patientForms.drvMedicalForm.sectionOne.drgGroup}
            maxLength={DRG_CODE_CHARACTER_LIMIT}
            name="drg_code"
            placeholder=" "
          />
        ),
        position: 3,
      },
      icd_code_1_code: {
        input: ({ translations }) => (
          <ControlledInput
            label={
              translations.patientForms.drvMedicalForm.sectionOne.diagnosisIcd
            }
            maxLength={ICD_CODE_CHARACTER_LIMIT}
            name="icd_code_1_code"
            placeholder=" "
          />
        ),
        position: 2,
      },
      main_diagnosis: {
        input: ({ translations }) => (
          <ControlledTextarea
            label={
              translations.patientForms.drvMedicalForm.sectionOne
                .descriptionDiagnosis
            }
            name="main_diagnosis"
          />
        ),
        position: 1,
      },
    },
    position: 2,
    title: ({ translations }) =>
      translations.patientForms.drvMedicalForm.sectionOne.title,
  },
  // Section Two: Complications and Therapy
  {
    fields: {
      complications: {
        input: ({ translations }) => (
          <ControlledTextarea
            label={
              translations.patientForms.drvMedicalForm.sectionTwo
                .descriptionComplications
            }
            name="complications"
          />
        ),
        position: 1,
      },
      current_therapy: {
        input: ({ translations }) => (
          <ControlledTextarea
            label={
              translations.patientForms.drvMedicalForm.sectionTwo
                .descriptionTherapies
            }
            name="current_therapy"
          />
        ),
        position: 2,
      },
      other_measures: {
        input: OtherMeasuresField,
        position: 3,
      },
    },
    position: 3,
    title: ({ translations }) =>
      translations.patientForms.drvMedicalForm.sectionTwo.title,
  },
  // Section Three: Secondary Diagnosis
  {
    fields: {
      secondary_diagnosis_selected: {
        input: SecondaryDiagnosisSelectedField,
        position: 1,
      },
    },
    position: 4,
    title: ({ translations }) =>
      translations.patientForms.drvMedicalForm.sectionThree.title,
  },
  // Section Four: Clinical Findings
  {
    fields: {
      clinical_findings: {
        input: ({ translations }) => (
          <ControlledTextarea
            label={translations.patientForms.drvMedicalForm.descriptionHistory}
            name="clinical_findings"
          />
        ),
        position: 1,
      },
      patient_height: {
        input: ({ translations }) => (
          <ControlledInput
            endContent={
              <span className="opacity-60">
                {translations.abbreviations.centimeter.abbreviation}
              </span>
            }
            label={translations.patientForms.drvMedicalForm.sectionFour.height}
            name="patient_height"
            placeholder=" "
            type="number"
          />
        ),
        position: 3,
      },
      patient_weight: {
        input: ({ translations }) => (
          <ControlledInput
            endContent={
              <span className="opacity-60">
                {translations.abbreviations.kilogram.abbreviation}
              </span>
            }
            label={translations.patientForms.drvMedicalForm.sectionFour.weight}
            name="patient_weight"
            placeholder=" "
            type="number"
          />
        ),
        position: 2,
      },
    },
    position: 5,
    title: ({ translations }) =>
      translations.patientForms.drvMedicalForm.sectionFour.title,
  },
  // Section Five: Patient Capabilities
  {
    fields: {
      carelevel_selected: {
        input: CarelevelSelectedField,
        position: 11,
      },
      eat_without_help: {
        input: ({ translations }) => {
          const options = [
            {
              label: translations.actions.no,
              value: PREDICAMENT_STATE_NO,
            },
            {
              label: translations.actions.yes,
              value: PREDICAMENT_STATE_YES,
            },
          ];

          return (
            <ControlledRadioGroup
              className="w-full"
              label={translations.patientForms.drvMedicalForm.sectionFive.eat}
              name="eat_without_help"
              options={options}
              size="sm"
            />
          );
        },
        position: 1,
      },
      fecal_incontinence: {
        input: ({ translations }) => {
          const options = [
            {
              label: translations.actions.no,
              value: PREDICAMENT_STATE_NO,
            },
            {
              label: translations.actions.yes,
              value: PREDICAMENT_STATE_YES,
            },
          ];

          return (
            <ControlledRadioGroup
              className="w-full"
              label={
                translations.patientForms.drvMedicalForm.sectionFive
                  .incontinence
              }
              name="fecal_incontinence"
              options={options}
              size="sm"
            />
          );
        },
        position: 8,
      },
      infections_selected: {
        input: InfectionsSelectedField,
        position: 5,
      },
      mobility_walk_without_help: {
        input: ({ translations }) => {
          const options = [
            {
              label: translations.actions.no,
              value: PREDICAMENT_STATE_NO,
            },
            {
              label: translations.actions.yes,
              value: PREDICAMENT_STATE_YES,
            },
          ];

          return (
            <ControlledRadioGroup
              className="w-full"
              label={translations.patientForms.drvMedicalForm.sectionFive.move}
              name="mobility_walk_without_help"
              options={options}
              size="sm"
            />
          );
        },
        position: 3,
      },
      mobility_wheel_chair: {
        input: ({ translations }) => {
          const options = [
            {
              label: translations.actions.no,
              value: PREDICAMENT_STATE_NO,
            },
            {
              label: translations.actions.yes,
              value: PREDICAMENT_STATE_YES,
            },
          ];

          return (
            <ControlledRadioGroup
              className="w-full"
              label={
                translations.patientForms.drvMedicalForm.sectionFive.wheelchair
              }
              name="mobility_wheel_chair"
              options={options}
              size="sm"
            />
          );
        },
        position: 9,
      },
      needs_catheter_selected: {
        input: NeedsCatheterSelectedField,
        position: 7,
      },
      orientation_disoriented_selected: {
        input: OrientationDisorientedSelectedField,
        position: 10,
      },
      outpatient_not_possible_reason: {
        input: ({ translations }) => {
          const options = [
            {
              label:
                translations.patientForms.drvMedicalForm.sectionFive
                  .accompanyingPerson,
              value: OUTPATIENT_NOT_POSSIBLE_REASONS.ACCOMPANYING_PERSON,
            },
            {
              label: translations.patientForms.drvMedicalForm.sectionFive.taxi,
              value: OUTPATIENT_NOT_POSSIBLE_REASONS.TAXI,
            },
            {
              label:
                translations.patientForms.drvMedicalForm.sectionFive.lyingDown,
              value: OUTPATIENT_NOT_POSSIBLE_REASONS.AMBULANCE,
            },
          ];

          return (
            <ControlledCheckboxGroup
              label={
                translations.patientForms.drvMedicalForm.sectionFive
                  .medicalReason
              }
              name="outpatient_not_possible_reason"
              options={options}
              size="sm"
            />
          );
        },
        position: 13,
      },
      transport_type: {
        input: ({ translations }) => {
          const options = [
            {
              label:
                translations.patientForms.drvMedicalForm.sectionFive
                  .travelPublic,
              value: DRV_TRAVEL_TYPES.PUBLIC,
            },
            {
              label:
                translations.patientForms.drvMedicalForm.sectionFive.travelCar,
              value: DRV_TRAVEL_TYPES.CAR,
            },
          ];

          return (
            <ControlledCheckboxGroup
              label={
                translations.patientForms.drvMedicalForm.sectionFive.travel
              }
              name="transport_type"
              options={options}
              size="sm"
            />
          );
        },
        position: 12,
      },
      urinary_incontinence: {
        input: ({ translations }) => {
          const options = [
            {
              label: translations.actions.no,
              value: PREDICAMENT_STATE_NO,
            },
            {
              label: translations.actions.yes,
              value: PREDICAMENT_STATE_YES,
            },
          ];

          return (
            <ControlledRadioGroup
              className="w-full"
              label={
                translations.patientForms.drvMedicalForm.sectionFive
                  .urinalIncontinence
              }
              name="urinary_incontinence"
              options={options}
              size="sm"
            />
          );
        },
        position: 6,
      },
      wash_and_dress_without_help: {
        input: ({ translations }) => {
          const options = [
            {
              label: translations.actions.no,
              value: PREDICAMENT_STATE_NO,
            },
            {
              label: translations.actions.yes,
              value: PREDICAMENT_STATE_YES,
            },
          ];

          return (
            <ControlledRadioGroup
              className="w-full"
              label={translations.patientForms.drvMedicalForm.sectionFive.wash}
              name="wash_and_dress_without_help"
              options={options}
              size="sm"
            />
          );
        },
        position: 2,
      },
      wound_closed: {
        input: ({ translations }) => {
          const options = [
            {
              label: translations.actions.no,
              value: PREDICAMENT_STATE_NO,
            },
            {
              label: translations.actions.yes,
              value: PREDICAMENT_STATE_YES,
            },
          ];

          return (
            <ControlledRadioGroup
              className="w-full"
              label={translations.patientForms.drvMedicalForm.sectionFive.wound}
              name="wound_closed"
              options={options}
              size="sm"
            />
          );
        },
        position: 4,
      },
    },
    position: 6,
    title: ({ translations }) =>
      translations.patientForms.drvMedicalForm.sectionFive.title,
  },
  // Section Six: Insurance and Banking
  {
    fields: {
      hospital_bank_holder: {
        input: ({ translations }) => (
          <ControlledInput
            label={
              translations.patientForms.drvMedicalForm.sectionSix.accountHolder
            }
            name="hospital_bank_holder"
            placeholder=" "
          />
        ),
        position: 2,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.drvMedicalForm.sectionSix
              .subtitleBankAccountInformation,
        },
      },
      hospital_bank_name: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.patientForms.drvMedicalForm.sectionSix.bankName}
            name="hospital_bank_name"
            placeholder=" "
          />
        ),
        position: 3,
      },
      hospital_billing_address: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.patientForms.drvMedicalForm.sectionSix.address}
            name="hospital_billing_address"
            placeholder=" "
          />
        ),
        position: 6,
      },
      hospital_iban: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.patientForms.drvMedicalForm.sectionSix.iban}
            name="hospital_iban"
            placeholder=" "
          />
        ),
        position: 4,
      },
      hospital_ik_number: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.patientForms.drvMedicalForm.sectionSix.ikNumber}
            maxLength={IK_NUMBER_CHARACTER_LIMIT}
            name="hospital_ik_number"
            placeholder=" "
          />
        ),
        position: 5,
      },
      insurance_delivery_address: {
        input: ({ translations }) => (
          <ControlledSelect
            items={INSURANCE_DELIVERY_ADDRESS_OPTIONS}
            label={
              translations.patientForms.drvMedicalForm.sectionSix
                .addressPensionInsurance
            }
            name="insurance_delivery_address"
            renderItem={(item) => item.label}
          />
        ),
        position: 1,
      },
    },
    position: 7,
    title: ({ translations }) =>
      translations.patientForms.drvMedicalForm.sectionSix.title,
  },
  // Section Seven: Remarks
  {
    fields: {
      drv_remarks: {
        input: ({ translations }) => (
          <ControlledTextarea
            label={
              translations.patientForms.drvMedicalForm.sectionSeven
                .remarksDescription
            }
            name="drv_remarks"
          />
        ),
        position: 1,
      },
    },
    position: 8,
    title: ({ translations }) =>
      translations.patientForms.drvMedicalForm.sectionSeven.title,
  },
];
